import {
  PaymentAmexIcon,
  PaymentBitcoinIcon,
  PaymentEthereumIcon,
  PaymentMasterIcon,
  PaymentPaypalIcon,
  PaymentTetherIcon,
  PaymentUnionpayIcon,
  PaymentUSDCIcon,
  PaymentVisaIcon,
} from "@assets";
import { Box, useRadio, useRadioGroup, useStyleConfig } from "@chakra-ui/react";
import WalletIcon from "@icons/WalletIcon";
import { PaymentMethod, Transaction, TransactionParam } from "@models/payment";
import { useEffect } from "react";
import { NextImage } from "../../../../../components/NextImage/NextImage";
import { usePaymentStore } from "../../../payment";
import CheckoutSectionWrapper from "./CheckoutSectionWrapper";

const _t = (key?: string) => key || "";

const Icon = (subPaymentMethod: string) => {
  switch (subPaymentMethod) {
    case "Visa":
      return PaymentVisaIcon;
    case "Mastercard":
      return PaymentMasterIcon;
    case "Amex":
      return PaymentAmexIcon;
    case "Unionpay":
      return PaymentUnionpayIcon;
    case "Bitcoin":
      return PaymentBitcoinIcon;
    case "Ethereum":
      return PaymentEthereumIcon;
    case "Tether":
      return PaymentTetherIcon;
    case "USDC":
      return PaymentUSDCIcon;
    case "Paypal":
      return PaymentPaypalIcon;
  }
};

const PaymentMethodIcons = (paymentMethod: PaymentMethod) => {
  switch (paymentMethod) {
    case PaymentMethod.STRIPE:
      return ["Visa", "Mastercard", "Amex", "Unionpay"];
    case PaymentMethod.COINGATE:
      return ["Bitcoin", "Ethereum", "Tether", "USDC"];
    case PaymentMethod.PAYPAL:
      return ["Paypal"];
  }
};

const PaymentMethodName = (paymentMethod: PaymentMethod) => {
  switch (paymentMethod) {
    case PaymentMethod.STRIPE:
      return _t("Credit Card");
    case PaymentMethod.COINGATE:
      return _t("Crypto");
    case PaymentMethod.PAYPAL:
      return _t("PayPal");
  }
};

function PaymentCard(props) {
  const { variant, ...rest } = props;

  const styles = useStyleConfig("PaymentCard", { variant });

  // Pass the computed styles into the `__css` prop
  return <Box __css={styles} {...rest} />;
}

function RadioCard(props) {
  const { getInputProps, getCheckboxProps } = useRadio(props);

  const input = getInputProps();
  const checkbox = getCheckboxProps();

  return (
    <Box as="label">
      <input {...input} />
      <PaymentCard {...checkbox}>{props.children}</PaymentCard>
    </Box>
  );
}

const CheckoutPaymentMethod = ({
  onChange,
  paymentMethods,
}: {
  onChange: (paymentMethod: PaymentMethod) => void;
  paymentMethods: PaymentMethod[];
}) => {
  const defaultValue = paymentMethods.includes(PaymentMethod.STRIPE)
    ? PaymentMethod.STRIPE
    : paymentMethods[0];

  const { getRootProps, getRadioProps, setValue } = useRadioGroup({
    name: "paymentMethod",
    defaultValue,
    onChange,
  });

  const group = getRootProps();

  useEffect(() => {
    const _transaction = usePaymentStore.getState().transaction;
    if (!_transaction) return;
    if (_transaction.paymentMethod) {
      setValue(_transaction.paymentMethod);
    } else {
      _transaction.paymentMethod = defaultValue;
    }
    usePaymentStore.setState({
      transaction: new Transaction(_transaction.json as TransactionParam),
    });
  }, []);

  return (
    <CheckoutSectionWrapper title={_t("Payment Method")} icon={WalletIcon}>
      <div className="flex flex-col items-center justify-center w-full h-full">
        <div
          className="flex flex-col sm:flex-row items-center justify-center w-full h-full gap-4"
          {...group}
        >
          {paymentMethods.map((value: PaymentMethod) => {
            const radio = getRadioProps({ value });
            return (
              <RadioCard key={value} {...radio}>
                <div className="flex flex-row items-center justify-center w-full h-full">
                  {PaymentMethodIcons(value)?.map((paymentIcon, index) => (
                    <div
                      key={index}
                      className="flex flex-row items-center justify-center w-full h-full"
                    >
                      <NextImage
                        className="w-7 mr-1"
                        src={Icon(paymentIcon)}
                        alt={paymentIcon}
                      />
                    </div>
                  ))}
                </div>
                {PaymentMethodName(value)}
              </RadioCard>
            );
          })}
        </div>
      </div>
    </CheckoutSectionWrapper>
  );
};
export default CheckoutPaymentMethod;
